import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { ProjectContext } from "../ProjectContext";

/**
 * Компоненты:
 * - ReraitPage            — контейнер страницы
 * - ReraitToolbar         — верхняя панель с кнопкой "Добавить задание" и обновлением
 * - ReraitList            — таблица заданий
 * - ReraitModal           — модалка (create/edit)
 * - ReraitFormFields      — поля формы (используются в модалке)
 *
 * Мобильная адаптивность: таблица скроллится горизонтально; модалка — полноэкранная на мобилках.
 */

const METRICS = [
  { value: "uniqViews", label: "Контакты" },
  { value: "uniqContacts", label: "Просмотры" },
  { value: "views_30_days", label: "показы за 30 дней" },
  
];

const ORDERS = [
  { value: "random", label: "Случайный" },
  { value: "sequential", label: "По очереди" },
];

const defaultForm = () => ({
  id: undefined,
  enabled: true,
  randomizierbool: false,
  projectId: "",
  usedPage: "",  // <-- "__all__" = все листы
  periodDays: 30,
  metric: "uniqContacts",
  threshold: 100,
  dailyLimit: 20,
  selectionOrder: "random",
  runAt: "03:00",
});

export default function ReraitPage() {
  const { projects, fetchProjects, projectsloading } = useContext(ProjectContext);
  

 const normalizedProjects = useMemo(
   () => (projects ?? []).map((p) => ({
     id: String(p.id),
     name: p.project_name,
     used_pages: p.used_pages || "",     // <-- добавили
   })),
   [projects]
 );


  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editData, setEditData] = useState(null); // null => create, объект => edit
  const modalInitialData = useMemo(() => mapJobToForm(editData), [editData])

  const usedProjectIds = useMemo(() => {
    return new Set(jobs.map(j => String(j.project_id)));
  }, [jobs]);


  useEffect(() => {
    if (typeof fetchProjects === "function" && (!projects || projects.length === 0)) {
      fetchProjects();
    }
  }, [fetchProjects]);

  // --- API helpers ---
  async function apiList() {
    setLoading(true); setError("");
    try {
      const res = await fetch("/api/rerait/", { method: "GET" });
      if (!res.ok) throw new Error(`List failed: ${res.status}`);
      const data = await res.json();
      setJobs(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || "Ошибка загрузки заданий");
    } finally {
      setLoading(false);
    }
  }

  async function apiCreate(payload) {
    const res = await fetch("/api/rerait/rerait", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`Create failed: ${res.status}`);
    return res.json();
  }

  async function apiUpdate(payload) {
    const res = await fetch(`/api/rerait/rerait`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload), // { id, ...fields }
    });
    if (!res.ok) throw new Error(`Update failed: ${res.status}`);
    return res.json();
  }

  async function apiDelete(id) {
    const res = await fetch(`/api/rerait/rerait`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id }),
    });
    if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
    return true;
  }

  useEffect(() => { apiList(); }, []);

  function openCreate() {
    setEditData(null);
    setIsModalOpen(true);
  }
  function openEdit(job) {
    setEditData(job);
    setIsModalOpen(true);
  }
  function closeModal() {
    setIsModalOpen(false);
  }

  async function handleRemove(job) {
    if (!window.confirm("Удалить задание?")) return;
    try {
      setLoading(true); setError("");
      await apiDelete(job.id);
      setJobs((prev) => prev.filter((j) => j.id !== job.id));
    } catch (e) {
      setError(e.message || "Ошибка удаления");
    } finally {
      setLoading(false);
    }
  }

  function mapJobToForm(job) {
  if (!job) return defaultForm();
 const raw = job.used_page;
 const normalized =
   raw == null
     ? ""
     : (typeof raw === "string" && ["none", "null"].includes(raw.toLowerCase()))
       ? ""
       : String(raw);

  return {
    id: job.id,
    enabled: Boolean(job.enabled),
    randomizierbool:  Boolean(job.randomizierbool),
    projectId: String(job.project_id),
    usedPage: normalized,
    periodDays: Number(job.period_days),
    metric: job.metric,
    threshold: Number(job.threshold),
    dailyLimit: Number(job.daily_limit),
    selectionOrder: job.selection_order,
    runAt: job.run_at || "03:00",
  };
}


  async function handleSubmit(form) {
    // валидация
    const err = validateForm(form);
    if (err) {
      alert(err);
      return;
    }
    const payload = {
      project_id: Number(form.projectId),
      used_page: form.usedPage ? form.usedPage : null,
      period_days: Number(form.periodDays),
      metric: form.metric,
      threshold: Number(form.threshold),
      daily_limit: Number(form.dailyLimit),
      selection_order: form.selectionOrder,
      run_at: form.runAt,
      enabled: Boolean(form.enabled),
      randomizierbool:  Boolean(form.randomizierbool),
    };

    try {
      setLoading(true); setError("");
      if (form.id != null) {
        const updated = await apiUpdate({ id: form.id, ...payload });
        setJobs((prev) => prev.map((j) => (j.id === updated.id ? updated : j)));
      } else {
        const created = await apiCreate(payload);
        setJobs((prev) => [created, ...prev]);
      }
      closeModal();
    } catch (e) {
      setError(e.message || "Ошибка сохранения");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={styles.page}>
      <InlineStyles />

      <div style={styles.headerRow}>
        <h2 style={styles.h2}>Авто-рерайт объявлений</h2>
        <ReraitToolbar
          loading={loading || projectsloading}
          onRefresh={apiList}
          onAdd={openCreate}
        />
      </div>

      {error && (
        <div style={styles.alertError}>{error}</div>
      )}

      <ReraitList
        jobs={jobs}
        projects={normalizedProjects}
        onEdit={openEdit}
        onDelete={handleRemove}
      />

      <ReraitModal
        open={isModalOpen}
        onClose={closeModal}
        projects={normalizedProjects}
        // initialData={mapJobToForm(editData)}
        initialData={modalInitialData}
        onSubmit={handleSubmit}
        usedProjectIds={usedProjectIds}
      />
    </div>
  );
}

/* -------------------- Subcomponents -------------------- */

function ReraitToolbar({ loading, onRefresh, onAdd }) {
  return (
    <div style={styles.toolbar}>
      <button onClick={onAdd} style={styles.btnPrimary} disabled={loading}>
        + Добавить задание
      </button>
      <button onClick={onRefresh} style={styles.btnGhost} disabled={loading}>
        Обновить список
      </button>
    </div>
  );
}

function formatUsedPage(v) {
  return !v ? "Все" : v; // ""/null -> "Все"
}

function ReraitList({ jobs, projects, onEdit, onDelete }) {
  if (!jobs?.length) {
    return <div style={styles.empty}>Пока нет заданий</div>;
  }
  return (
    <div style={styles.tableWrap}>
      <table style={styles.table}>
        <thead>
          <tr style={{ textAlign: "left", background: "#fafafa" }}>
            <th style={styles.cell}>Проект</th>
            <th style={styles.cell}>Лист</th>
            <th style={styles.cell}>Период</th>
            <th style={styles.cell}>Метрика</th>
            <th style={styles.cell}>Порог</th>
            <th style={styles.cell}>Лимит/день</th>
            <th style={styles.cell}>Порядок</th>
            <th style={styles.cell}>Запуск</th>
            <th style={styles.cell}>Статус</th>
            <th style={styles.cell}>Действия</th>
          </tr>
        </thead>
        <tbody>
          {jobs.map((j) => (
            <tr key={j.id}>
              <td style={styles.cell}>{projectName(projects, j.project_id)}</td>
              <td style={styles.cell}>{formatUsedPage(j.used_page)}</td> {/* <-- новый столбец */}
              <td style={styles.cell}>{j.period_days} дн.</td>
              <td style={styles.cell}>{metricLabel(j.metric)}</td>
              <td style={styles.cell}>{j.threshold}</td>
              <td style={styles.cell}>{j.daily_limit}</td>
              <td style={styles.cell}>{orderLabel(j.selection_order)}</td>
              <td style={styles.cell}>{j.run_at}</td>
              <td style={styles.cell}>{j.enabled ? "Активно" : "Пауза"}</td>
              <td style={styles.cell}>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => onEdit(j)} style={styles.btnGhost}>Редактировать</button>
                  <button onClick={() => onDelete(j)} style={styles.btnDanger}>Удалить</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReraitModal({ open, onClose, projects, initialData, onSubmit, usedProjectIds }) {
  const dialogRef = useRef(null);
  const [form, setForm] = useState(initialData || defaultForm());

  useEffect(() => {
    setForm(initialData || defaultForm());
  }, [initialData]);


  // Управление native <dialog>
  useEffect(() => {
    const dlg = dialogRef.current;
    if (!dlg) return;
    if (open && !dlg.open) dlg.showModal();
    if (!open && dlg.open) dlg.close();
  }, [open]);

  function handleCancel(e) {
    e?.preventDefault?.();
    onClose();
  }

  function handleSubmit(e) {
    e.preventDefault();
    onSubmit(form);
  }

  return (
    <dialog ref={dialogRef} style={styles.dialog} onCancel={handleCancel}>
      <form onSubmit={handleSubmit} style={styles.modalCard}>
        <div style={styles.modalHeader}>
          <div style={styles.modalTitle}>{form.id ? "Редактировать задание" : "Новое задание"}</div>
          <button type="button" onClick={handleCancel} style={styles.btnIcon} aria-label="Закрыть">✕</button>
        </div>

        <ReraitFormFields
          form={form}
          setForm={setForm}
          projects={projects}
          usedProjectIds={usedProjectIds}
        />

        <div style={styles.modalActions}>
          <button type="submit" style={styles.btnPrimary}>Сохранить</button>
          <button type="button" onClick={handleCancel} style={styles.btnGhost}>Отмена</button>
        </div>
      </form>
    </dialog>
  );
}
function getSheetsFromUsedPages(used_pages) {
  if (!used_pages) return [];
  // "Лист1 | Лист2" ИЛИ "Лист1|Лист2"
 const parts = used_pages
   .split("|")
   .map(s => s.trim())
   .filter(Boolean)
   .filter(s => !["none","null"].includes(s.toLowerCase()));
  // уникальные
  return Array.from(new Set(parts));
}


function ReraitFormFields({ form, setForm, projects, usedProjectIds }) {
  const sheets = React.useMemo(() => {
    const proj = projects.find(p => p.id === String(form.projectId));
    return getSheetsFromUsedPages(proj?.used_pages || "");
  }, [projects, form.projectId]);

  // если выбрали другой проект и текущий лист больше не валиден — сбросить на "Все"
  useEffect(() => {
  if (form.usedPage && sheets.length && !sheets.includes(form.usedPage)) {
    setForm(s => ({ ...s, usedPage: "" })); // "" = Все
  }
  }, [sheets]);

  return (
    <div style={styles.grid}>
      <div>
        <label style={styles.label}>Проект</label>
        <select
          value={form.projectId}
          onChange={(e) => setForm((s) => ({ ...s, projectId: e.target.value }))}
          style={styles.input}
          required
        >
          <option value="">— выбрать проект —</option>
          {/* {projects.map((p) => (
            <option key={p.id} value={p.id}>{p.name} (ID: {p.id})</option>
          ))} */}
          {projects.map((p) => {
              const isUsed = usedProjectIds.has(p.id) && String(form.projectId) !== p.id;

              return (
                <option
                  key={p.id}
                  value={p.id}
                  disabled={isUsed}
                >
                  {p.name} (ID: {p.id}) {isUsed ? "— правило уже существует" : ""}
                </option>
              );
            })}
                    </select>
      </div>

           {/* Новый блок: выбор листа */}
      <div>
        <label style={styles.label}>Лист таблицы</label>
        <select
          value={form.usedPage}
          onChange={(e) => setForm((s) => ({ ...s, usedPage: e.target.value }))}
          style={styles.input}
          disabled={!form.projectId}   // без проекта не выбираем лист
        >
          <option value="">Все</option>
          {sheets.map((name) => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>
      </div>

      <div>
        <label style={styles.label}>Период (дней)</label>
        <input
          type="number"
          min={1}
          value={form.periodDays}
          onChange={(e) => setForm((s) => ({ ...s, periodDays: Number(e.target.value) }))}
          style={styles.input}
          required
        />
      </div>

      <div>
        <label style={styles.label}>Параметр для рерайта</label>
        <select
          value={form.metric}
          onChange={(e) => setForm((s) => ({ ...s, metric: e.target.value }))}
          style={styles.input}
          required
        >
          {METRICS.map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label style={styles.label}>Порог выбранной метрики</label>
        <input
          type="number"
          min={0}
          value={form.threshold}
          onChange={(e) => setForm((s) => ({ ...s, threshold: Number(e.target.value) }))}
          style={styles.input}
          required
        />
      </div>

      <div>
        <label style={styles.label}>Лимит обновлений в день</label>
        <input
          type="number"
          min={1}
          value={form.dailyLimit}
          onChange={(e) => setForm((s) => ({ ...s, dailyLimit: Number(e.target.value) }))}
          style={styles.input}
          required
        />
      </div>

      <div>
        <label style={styles.label}>Порядок выбора</label>
        <select
          value={form.selectionOrder}
          onChange={(e) => setForm((s) => ({ ...s, selectionOrder: e.target.value }))}
          style={styles.input}
          required
        >
          {ORDERS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label style={styles.label}>Время ежедневного запуска(МСК)</label>
        <input
          type="time"
          value={form.runAt}
          onChange={(e) => setForm((s) => ({ ...s, runAt: e.target.value }))}
          style={styles.input}
          required
        />
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input
          id="enabled"
          type="checkbox"
          checked={form.enabled}
          onChange={(e) => setForm((s) => ({ ...s, enabled: e.target.checked }))}
        />
        <label htmlFor="enabled">Задание активно</label>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input
          id="randomizierbool"
          type="checkbox"
          checked={form.randomizierbool}
          onChange={(e) => setForm((s) => ({ ...s, randomizierbool: e.target.checked }))}
        />
        <label htmlFor="randomizierbool">заполнены колонки рандомайзера?</label>
      </div>
    </div>
  );
}

/* -------------------- Helpers -------------------- */

function validateForm(form) {
  if (!form.projectId) return "Выберите проект";
  if (!form.periodDays || form.periodDays <= 0) return "Укажите корректный период";
  if (!form.metric) return "Выберите параметр для рерайта";
  if (form.threshold == null || form.threshold < 0) return "Укажите порог";
  if (!form.dailyLimit || form.dailyLimit <= 0) return "Укажите дневной лимит";
  if (!form.runAt) return "Укажите время запуска(МСК)";
  return "";
}

function projectName(list, id) {
  const p = list.find((x) => x.id === String(id));
  return p ? `${p.name} (ID: ${p.id})` : `#${id}`;
}
function metricLabel(v) { return METRICS.find((m) => m.value === v)?.label || v; }
function orderLabel(v) { return ORDERS.find((o) => o.value === v)?.label || v; }

/* -------------------- Styles -------------------- */

function InlineStyles() {
  return (
    <style>{`
      @media (max-width: 640px) {
        .modal-card {
          width: 100% !important;
          height: 100% !important;
          margin: 0 !important;
          border-radius: 0 !important;
        }
      }
    `}</style>
  );
}

const styles = {
  page: { maxWidth: 1000, margin: "0 auto", padding: 16 },
  h2: { fontSize: 22, fontWeight: 600, margin: 0 },
  headerRow: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 },
  toolbar: { display: "flex", gap: 8, flexWrap: "wrap" },

  tableWrap: { overflowX: "auto", border: "1px solid #eee", borderRadius: 8 },
  table: { width: "100%", borderCollapse: "collapse", minWidth: 720 },
  cell: { padding: "8px 10px", borderBottom: "1px solid #eee" },

  empty: { padding: 12, border: "1px dashed #ccc", borderRadius: 8 },

  alertError: { padding: 12, background: "#fff5f5", border: "1px solid #ffd6d6", color: "#b00020", marginBottom: 12 },

  btnPrimary: { padding: "8px 12px", background: "#111827", color: "#fff", borderRadius: 8, border: "none", cursor: "pointer" },
  btnGhost: { padding: "8px 12px", background: "#f3f4f6", color: "#111", borderRadius: 8, border: "1px solid #e5e7eb", cursor: "pointer" },
  btnDanger: { padding: "8px 12px", background: "#ef4444", color: "#fff", borderRadius: 8, border: "none", cursor: "pointer" },
  btnIcon: { background: "transparent", border: "none", cursor: "pointer", fontSize: 18, lineHeight: 1 },

  dialog: {
    padding: 0, border: "none", borderRadius: 12, width: "min(720px, 95vw)",
    maxWidth: "95vw",
  },
  modalCard: {
    display: "flex", flexDirection: "column", gap: 12, padding: 16,
    width: "min(720px, 95vw)", maxWidth: "95vw", boxSizing: "border-box"
  },
  modalHeader: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  modalTitle: { fontSize: 18, fontWeight: 600 },
  modalActions: { display: "flex", gap: 8, justifyContent: "flex-end" },

  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
  },
  label: { display: "block", marginBottom: 4, color: "#333" },
  input: { width: "100%", padding: 8, border: "1px solid #e5e7eb", borderRadius: 8, boxSizing: "border-box" },
};

/* Мобильная сетка */
styles.grid["@media(max-width:640px)"] = { gridTemplateColumns: "1fr" };

/* Класс для мобильной модалки */
styles.modalCard.className = "modal-card";
